import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { PageHeader } from '@/components/app/PageHeader';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { User as UserIcon, Phone, MapPin, Mail, ShieldCheck, KeyRound, Save } from 'lucide-react';
import { toast } from 'sonner';
import { ROLE_LABEL, KYC_STATUS_LABEL, KYC_STATUS_COLOR } from '@/lib/constants';
import { PasswordRequirements } from '@/components/auth/PasswordRequirements';
import { isPasswordStrong, PASSWORD_POLICY_MESSAGE } from '@/lib/passwordPolicy';

export default function ProfilePage() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({ name: '', mobile: '', address: '' });
  const [pwd, setPwd] = useState({ current_password: '', new_password: '', confirm: '' });
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPwd, setSavingPwd] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get('/auth/me').then((r) => {
      if (cancelled) return;
      setProfile(r.data);
      const p = r.data.profile || {};
      setForm({
        name: r.data.name || '',
        mobile: r.data.mobile || p.mobile || '',
        address: r.data.address || p.address || '',
      });
    }).catch(() => toast.error('Could not load profile'));
    return () => { cancelled = true; };
  }, []);

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      await api.put('/auth/me', form);
      toast.success('Profile updated');
      // refresh local cache
      const me = await api.get('/auth/me');
      setProfile(me.data);
      try {
        const cached = JSON.parse(localStorage.getItem('pmcrm_user') || '{}');
        cached.name = me.data.name;
        localStorage.setItem('pmcrm_user', JSON.stringify(cached));
      } catch (e) { /* ignore */ }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    } finally { setSavingProfile(false); }
  };

  const changePassword = async () => {
    if (!pwd.current_password) return toast.error('Enter your current password');
    if (pwd.new_password !== pwd.confirm) return toast.error('New password and confirmation do not match');
    if (!isPasswordStrong(pwd.new_password)) return toast.error(PASSWORD_POLICY_MESSAGE);
    setSavingPwd(true);
    try {
      await api.post('/auth/me/change-password', { current_password: pwd.current_password, new_password: pwd.new_password });
      toast.success('Password updated. Please sign in again with the new password.');
      setPwd({ current_password: '', new_password: '', confirm: '' });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update password');
    } finally { setSavingPwd(false); }
  };

  if (!user) return null;
  const kycStatus = profile?.profile?.kyc_status;

  return (
    <div>
      <PageHeader title="My Profile" subtitle="View and update your account details, contact info, and password" />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Identity card */}
        <Card className="p-5 lg:col-span-1 space-y-3">
          <div className="flex items-center gap-3">
            <div className="h-14 w-14 rounded-full grid place-items-center bg-[hsl(var(--role-accent))] text-white text-lg font-semibold">
              {(user.name || 'U').split(' ').map((s) => s[0]).slice(0, 2).join('').toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="text-base font-semibold truncate">{user.name}</div>
              <div className="text-xs text-muted-foreground truncate">{user.email}</div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 pt-1">
            <Badge variant="outline" className="font-medium">{ROLE_LABEL[user.role] || user.role}</Badge>
            {kycStatus && (
              <Badge variant="outline" className={`font-medium ${KYC_STATUS_COLOR[kycStatus] || ''}`}>
                <ShieldCheck className="h-3 w-3 mr-1" /> KYC: {KYC_STATUS_LABEL[kycStatus] || kycStatus}
              </Badge>
            )}
          </div>
          <div className="text-xs text-muted-foreground space-y-1.5 pt-2 border-t">
            <div className="flex items-center gap-2"><Mail className="h-3.5 w-3.5" /> {user.email}</div>
            <div className="flex items-center gap-2"><Phone className="h-3.5 w-3.5" /> {form.mobile || profile?.profile?.mobile || '—'}</div>
            <div className="flex items-start gap-2"><MapPin className="h-3.5 w-3.5 mt-0.5" /> <span className="break-words">{form.address || profile?.profile?.address || '—'}</span></div>
          </div>
          <div className="text-[11px] text-muted-foreground pt-2 border-t">
            Email changes are restricted. Contact your Super-Admin if you need to update your email address.
          </div>
        </Card>

        {/* Edit panels */}
        <Card className="p-0 lg:col-span-2 overflow-hidden">
          <Tabs defaultValue="details">
            <TabsList className="m-3">
              <TabsTrigger value="details" data-testid="profile-tab-details"><UserIcon className="h-3.5 w-3.5 mr-1" /> Personal Details</TabsTrigger>
              <TabsTrigger value="password" data-testid="profile-tab-password"><KeyRound className="h-3.5 w-3.5 mr-1" /> Change Password</TabsTrigger>
            </TabsList>

            <TabsContent value="details" className="px-4 pb-4 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Full name</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="profile-name-input" />
                </div>
                <div>
                  <Label>Mobile number</Label>
                  <Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} placeholder="+91 ..." data-testid="profile-mobile-input" />
                </div>
                <div className="sm:col-span-2">
                  <Label>Address</Label>
                  <Textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Street, area, city, state, PIN" rows={3} data-testid="profile-address-input" />
                </div>
                <div className="sm:col-span-2">
                  <Label>Email <span className="text-muted-foreground text-xs">(read-only)</span></Label>
                  <Input value={user.email} disabled />
                </div>
              </div>
              <div className="flex justify-end">
                <Button onClick={saveProfile} disabled={savingProfile} data-testid="profile-save-button">
                  <Save className="h-4 w-4 mr-1" /> {savingProfile ? 'Saving…' : 'Save changes'}
                </Button>
              </div>
            </TabsContent>

            <TabsContent value="password" className="px-4 pb-4 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <Label>Current password</Label>
                  <Input type="password" value={pwd.current_password} onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })} data-testid="profile-current-password" />
                </div>
                <div>
                  <Label>New password</Label>
                  <Input type="password" value={pwd.new_password} onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })} placeholder="At least 8 characters" data-testid="profile-new-password" />
                </div>
                <div>
                  <Label>Confirm new password</Label>
                  <Input type="password" value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })} data-testid="profile-confirm-password" />
                </div>
              </div>
              <PasswordRequirements value={pwd.new_password} />
              <div className="flex justify-end">
                <Button onClick={changePassword} disabled={savingPwd} data-testid="profile-change-password-button">
                  <KeyRound className="h-4 w-4 mr-1" /> {savingPwd ? 'Updating…' : 'Update password'}
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        </Card>
      </div>
    </div>
  );
}
